import React, { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/solid';
import { useSettingsStore } from '../hooks/useSettingsStore';
import { useAuthStore } from '../hooks/useAuthStore';
import { authAPI } from '../services/api';
import Users from './Users';
import Stats from './Stats';

// Helper function for safe integer parsing with validation
function parseIntSafe(value: string, defaultValue: number, min?: number, max?: number): number {
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) {
    return defaultValue;
  }
  if (min !== undefined && parsed < min) {
    return min;
  }
  if (max !== undefined && parsed > max) {
    return max;
  }
  return parsed;
}

export default function Settings() {
  const {
    settings,
    apiKeys,
    engines,
    isLoading,
    isSaving,
    loadError,
    fetchSettings,
    updateSettings,
    saveSettings,
    saveAPIKey,
    testAPIKey,
  } = useSettingsStore();

  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('general');

  // API key input state (local to this component)
  const [keyInputs, setKeyInputs] = useState<Record<string, string>>({});
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<
    Record<string, { success: boolean; error?: string }>
  >({});

  // Local UI settings (stored in localStorage)
  const [submitKey, setSubmitKey] = useState<'enter' | 'ctrl-enter'>(() => {
    const saved = localStorage.getItem('quorum_submit_key');
    return saved === 'ctrl-enter' ? 'ctrl-enter' : 'enter';
  });

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // Local timeout input state to avoid immediate validation clamping
  const [timeoutInput, setTimeoutInput] = useState<string>('');
  const [timeoutError, setTimeoutError] = useState<boolean>(false);

  // Initialize timeout input when settings load
  useEffect(() => {
    if (settings?.default_timeout) {
      setTimeoutInput(settings.default_timeout.toString());
      setTimeoutError(false);
    }
  }, [settings?.default_timeout]);

  const handleSubmitKeyChange = (value: 'enter' | 'ctrl-enter') => {
    setSubmitKey(value);
    localStorage.setItem('quorum_submit_key', value);
  };

  const handleTimeoutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTimeoutInput(value);
    // Clear error state while typing
    if (timeoutError) {
      setTimeoutError(false);
    }
  };

  const handleTimeoutBlur = () => {
    const numValue = parseIntSafe(timeoutInput, settings?.default_timeout || 60, 10, 300);
    const isValid = !isNaN(parseInt(timeoutInput, 10)) &&
                    parseInt(timeoutInput, 10) >= 10 &&
                    parseInt(timeoutInput, 10) <= 300;

    if (isValid && numValue.toString() === timeoutInput) {
      // Valid input, update settings
      updateSettings({ default_timeout: numValue });
      setTimeoutError(false);
    } else {
      // Invalid input, show error and revert
      setTimeoutError(true);
      // Revert to current settings value after a short delay
      setTimeout(() => {
        setTimeoutInput((settings?.default_timeout || 60).toString());
        setTimeoutError(false);
      }, 2000);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(false);

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.');
      return;
    }

    setChangingPassword(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password.');
    } finally {
      setChangingPassword(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveKey = async (provider: string) => {
    const key = keyInputs[provider];
    if (!key) return;

    try {
      await saveAPIKey(provider, key);
      setKeyInputs((prev) => ({ ...prev, [provider]: '' }));
      setTestResults((prev) => {
        const updated = { ...prev };
        delete updated[provider];
        return updated;
      });
    } catch {
      // Error already handled by store
    }
  };

  const handleTestKey = async (provider: string) => {
    const key = keyInputs[provider];
    if (!key) return;

    setTesting(provider);
    const result = await testAPIKey(provider, key);
    setTestResults((prev) => ({
      ...prev,
      [provider]: result,
    }));
    setTesting(null);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Error state with retry
  if (loadError) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8">
        <XCircleIcon className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-semibold text-surface-300 mb-2">
          Failed to Load Settings
        </h2>
        <p className="text-surface-400 mb-4">
          There was a problem loading your settings. Please try again.
        </p>
        <button onClick={fetchSettings} className="btn-primary flex items-center gap-2">
          <ArrowPathIcon className="w-5 h-5" />
          Retry
        </button>
      </div>
    );
  }

  // Settings not loaded yet
  if (!settings) {
    return null;
  }

  const tabs = [
    { id: 'general', name: 'General' },
    ...(user?.is_admin ? [{ id: 'api-keys', name: 'API Keys' }] : []),
    { id: 'retention', name: 'Data Retention' },
    { id: 'statistics', name: 'Statistics' },
    ...(user?.is_admin ? [{ id: 'users', name: 'Users' }] : []),
  ];

  // Render tabs with special layouts as full pages
  if (activeTab === 'statistics') {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="p-6 pb-0">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold text-surface-100 mb-6">Settings</h1>
            <div className="flex gap-1 mb-6 bg-surface-800 p-1 rounded-lg w-fit">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-600 text-white'
                      : 'text-surface-400 hover:text-surface-200'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <Stats />
        </div>
      </div>
    );
  }

  // Render Users tab as a full page since it has its own layout
  if (activeTab === 'users' && user?.is_admin) {
    return (
      <div className="h-full flex flex-col overflow-hidden">
        <div className="p-6 pb-0">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold text-surface-100 mb-6">Settings</h1>
            <div className="flex gap-1 mb-6 bg-surface-800 p-1 rounded-lg w-fit">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-600 text-white'
                      : 'text-surface-400 hover:text-surface-200'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-hidden">
          <Users />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-surface-100 mb-6">Settings</h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-surface-800 p-1 rounded-lg w-fit">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary-600 text-white'
                  : 'text-surface-400 hover:text-surface-200'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </div>

        {/* General Settings */}
        {activeTab === 'general' && (
          <div className="card space-y-6">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Default Query Mode
              </label>
              <select
                value={settings.default_mode}
                onChange={(e) => updateSettings({ default_mode: e.target.value })}
                className="input-field"
              >
                <option value="quick">Quick (1 round)</option>
                <option value="deliberation">Deliberation (2 rounds)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Engine Timeout (seconds)
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={timeoutInput}
                  onChange={handleTimeoutChange}
                  onBlur={handleTimeoutBlur}
                  className={`input-field ${timeoutError ? 'border-red-500 focus:ring-red-500' : ''}`}
                  min={10}
                  max={300}
                  placeholder="10-300"
                />
                {timeoutError && (
                  <p className="text-red-400 text-sm mt-1">
                    Invalid timeout. Must be between 10 and 300 seconds.
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Judge Model
              </label>
              <select
                value={settings.judge_model}
                onChange={(e) => updateSettings({ judge_model: e.target.value })}
                className="input-field"
              >
                <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
                <option value="claude-opus-4-20250514">Claude Opus 4</option>
                <option value="gpt-4o">GPT-4o</option>
              </select>
            </div>

            {/* UI Preferences Section */}
            <div className="border-t border-surface-700 pt-6 mt-6">
              <h3 className="text-lg font-medium text-surface-200 mb-4">UI Preferences</h3>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Submit Query With
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="submitKey"
                      value="enter"
                      checked={submitKey === 'enter'}
                      onChange={() => handleSubmitKeyChange('enter')}
                      className="w-4 h-4 text-primary-600 bg-surface-700 border-surface-600 focus:ring-primary-500"
                    />
                    <span className="text-surface-300">Enter</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="submitKey"
                      value="ctrl-enter"
                      checked={submitKey === 'ctrl-enter'}
                      onChange={() => handleSubmitKeyChange('ctrl-enter')}
                      className="w-4 h-4 text-primary-600 bg-surface-700 border-surface-600 focus:ring-primary-500"
                    />
                    <span className="text-surface-300">Ctrl+Enter (or Cmd+Enter)</span>
                  </label>
                </div>
                <p className="text-xs text-surface-500 mt-1">
                  {submitKey === 'enter'
                    ? 'Press Enter to submit, Shift+Enter for new line'
                    : 'Press Ctrl+Enter (or Cmd+Enter on Mac) to submit, Enter for new line'}
                </p>
              </div>
            </div>

            <button onClick={saveSettings} disabled={isSaving} className="btn-primary">
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>

            {/* Change Password Section */}
            <div className="border-t border-surface-700 pt-6 mt-6">
              <h3 className="text-lg font-medium text-surface-200 mb-4">Change Password</h3>

              <div className="space-y-4 max-w-sm">
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-field"
                    minLength={6}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field"
                    minLength={6}
                  />
                </div>

                {passwordError && (
                  <p className="text-red-400 text-sm">{passwordError}</p>
                )}
                {passwordSuccess && (
                  <p className="text-green-400 text-sm">Password changed successfully.</p>
                )}

                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                  className="btn-primary"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* API Keys */}
        {activeTab === 'api-keys' && (
          <div className="space-y-4">
            {engines.map((engine) => {
              const keyStatus = apiKeys.find((k) => k.provider === engine.name);
              const testResult = testResults[engine.name];

              return (
                <div key={engine.name} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-surface-100 capitalize">
                        {engine.name}
                      </h3>
                      {keyStatus?.configured ? (
                        <CheckCircleIcon className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircleIcon className="w-5 h-5 text-surface-500" />
                      )}
                    </div>
                    <span className="text-sm text-surface-400">
                      {engine.default_model}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="password"
                      value={keyInputs[engine.name] || ''}
                      onChange={(e) =>
                        setKeyInputs((prev) => ({
                          ...prev,
                          [engine.name]: e.target.value,
                        }))
                      }
                      placeholder={
                        keyStatus?.configured ? '••••••••' : 'Enter API key'
                      }
                      className="input-field flex-1"
                    />
                    <button
                      onClick={() => handleTestKey(engine.name)}
                      disabled={
                        !keyInputs[engine.name] || testing === engine.name
                      }
                      className="btn-secondary flex items-center gap-1"
                    >
                      {testing === engine.name ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        'Test'
                      )}
                    </button>
                    <button
                      onClick={() => handleSaveKey(engine.name)}
                      disabled={!keyInputs[engine.name]}
                      className="btn-primary"
                    >
                      Save
                    </button>
                  </div>

                  {testResult && (
                    <div
                      className={`mt-2 text-sm ${
                        testResult.success ? 'text-green-400' : 'text-red-400'
                      }`}
                    >
                      {testResult.success
                        ? 'Connection successful!'
                        : testResult.error || 'Connection failed'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Data Retention */}
        {activeTab === 'retention' && (
          <div className="card space-y-6">
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Standalone Chat Retention (days)
              </label>
              <input
                type="number"
                value={settings.standalone_chat_retention}
                onChange={(e) =>
                  updateSettings({
                    standalone_chat_retention: parseIntSafe(
                      e.target.value,
                      settings.standalone_chat_retention,
                      1
                    ),
                  })
                }
                className="input-field"
                min={1}
              />
              <p className="text-xs text-surface-500 mt-1">
                Chats not in a project will be deleted after this many days
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Project Chat Retention (days, 0 = never)
              </label>
              <input
                type="number"
                value={settings.project_chat_retention}
                onChange={(e) =>
                  updateSettings({
                    project_chat_retention: parseIntSafe(
                      e.target.value,
                      settings.project_chat_retention,
                      0
                    ),
                  })
                }
                className="input-field"
                min={0}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-300 mb-1">
                Engine Response Retention (days)
              </label>
              <input
                type="number"
                value={settings.engine_response_retention}
                onChange={(e) =>
                  updateSettings({
                    engine_response_retention: parseIntSafe(
                      e.target.value,
                      settings.engine_response_retention,
                      1
                    ),
                  })
                }
                className="input-field"
                min={1}
              />
              <p className="text-xs text-surface-500 mt-1">
                Individual engine responses are kept for review
              </p>
            </div>

            <button onClick={saveSettings} disabled={isSaving} className="btn-primary">
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
